import compact from 'lodash/compact';
import clsx from 'clsx';
import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import FileDragOverlayProvider from 'core/context/fileDragOverlay';
import UploadIcon from 'core/icons/upload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';
import {
  Droppable,
} from '@hello-pangea/dnd';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import ToolTip from 'core/components/Tooltip';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  insurancesEventChannel,
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  toLocalUrls,
} from 'utils/files';
import InsuranceAttachmentActions from 'views/EMR/components/Insurance/InsuranceCard/InsuranceAttachmentActions';
import {
  useGetInsuranceAttachments,
} from 'views/EMR/components/Insurance/hooks';
import InsuranceCardMediaThumbnail from 'views/EMR/components/Insurance/InsuranceCard/InsuranceCardMediaThumbnail';
import useInsuranceAttachmentUpload from 'views/EMR/components/Insurance/InsuranceCard/useInsuranceAttachmentUpload';
import {
  getInsuranceCardDragId,
  InsuranceCardDragItem,
} from 'views/EMR/components/Insurance/insuranceCardDnD';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';

type Props = {
  insuranceId?: number;
  patientId?: number;
  activeDragSourceKey: string | null;
  droppableId: string;
  busyContainerKeys: string[];
  isDraggingInsuranceCard: boolean;
  shouldShowDragHint: boolean;
  registerContainerItems: (
    containerKey: string,
    items: InsuranceCardDragItem[],
  ) => void;
  registerRefetch?: (
    insuranceId: number,
    refetch: () => Promise<unknown>,
  ) => void;
  unregisterContainerItems: (containerKey: string) => void;
  unregisterRefetch?: (insuranceId: number) => void;
};

export default function InsuranceAttachments({
  insuranceId,
  patientId,
  activeDragSourceKey,
  droppableId,
  busyContainerKeys,
  isDraggingInsuranceCard,
  shouldShowDragHint,
  registerContainerItems,
  registerRefetch,
  unregisterContainerItems,
  unregisterRefetch,
}: Props) {
  const notify = useNotify();
  const dropAreaRef = useRef<HTMLDivElement | null>(null);

  const hasInsuranceId = (insuranceId ?? 0) > 0;
  const {
    data: attachments = [],
    isLoading,
    refetch,
  } = useGetInsuranceAttachments(insuranceId ?? 0);

  const draggableAttachments = useMemo(
    () => compact(attachments.map((attachment) => {
      const { media } = attachment;
      const mediaId = media?.mediaId ?? 0;
      const attachmentId = attachment.insuranceAttachmentId ?? 0;
      const hasMedia = mediaId > 0 && attachmentId > 0;
      let dragItem: InsuranceCardDragItem | null = null;

      if (hasMedia) {
        dragItem = {
          dragId: getInsuranceCardDragId(insuranceId ?? 0, mediaId),
          patientId: patientId ?? 0,
          mediaId,
          fileName: media?.fileName ?? 'Insurance card',
          previewMedia: media as MultiMediaResponse,
          sourceType: 'insurance',
          sourceInsuranceId: insuranceId,
          insuranceAttachmentId: attachmentId,
        };
      }

      return dragItem;
    })) as InsuranceCardDragItem[],
    [attachments, insuranceId, patientId],
  );

  const attachmentMediaIds = useMemo(() => new Set(
    draggableAttachments
      .map((attachment) => attachment.mediaId)
      .filter((mediaId): mediaId is number => !!mediaId),
  ), [draggableAttachments]);
  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.InsuranceCard);
  const cleanExtensions = useMemo(
    () => allowedFileTypes.split(',')
      .map((extension) => extension.trim().replace(/^\./, ''))
      .filter(Boolean),
    [allowedFileTypes],
  );
  const dropzoneAccept = useMemo(
    () => generateDropzoneAccept(cleanExtensions),
    [cleanExtensions],
  );
  const shouldShowSection = hasInsuranceId;
  const isMoveBusy = busyContainerKeys.includes(droppableId);
  const {
    uploadFiles,
    isUploading,
  } = useInsuranceAttachmentUpload({
    insuranceId,
    patientId,
    onUploadSuccess: refetch,
  });
  const isInteractionDisabled = isMoveBusy || isUploading;
  const isSourceContainer = activeDragSourceKey === droppableId;
  const isValidMoveTarget = isDraggingInsuranceCard
    && !isInteractionDisabled
    && !isSourceContainer;

  useEffect(() => {
    const unsubscribeFromMediaDeleted = mediaEventChannel.on('onMediaDeleted', (metaInfo) => {
      const hasDeletedAttachment = (metaInfo?.list ?? []).some((media) =>
        attachmentMediaIds.has(media?.mediaId ?? 0));

      if (hasDeletedAttachment) {
        refetch();
      }
    });

    const unsubscribeFromPatientInsuranceUpdated = insurancesEventChannel.on('onPatientInsuranceUpdated', () => {
      refetch();
    });

    return () => {
      unsubscribeFromMediaDeleted();
      unsubscribeFromPatientInsuranceUpdated();
    };
  }, [attachmentMediaIds, refetch]);

  useEffect(() => {
    if ((insuranceId ?? 0) > 0) {
      registerRefetch?.(insuranceId ?? 0, refetch);
    }

    return () => {
      if ((insuranceId ?? 0) > 0) {
        unregisterRefetch?.(insuranceId ?? 0);
      }
    };
  }, [insuranceId, refetch, registerRefetch, unregisterRefetch]);

  useEffect(() => {
    registerContainerItems(droppableId, draggableAttachments);

    return () => {
      unregisterContainerItems(droppableId);
    };
  }, [
    draggableAttachments,
    droppableId,
    registerContainerItems,
    unregisterContainerItems,
  ]);

  return shouldShowSection ? (
    <div className="px-4 pb-4 min-h-[8rem]">
      <div className="flex flex-col gap-2 border-t border-t-gray-100 pt-3">
        <div className="flex items-center gap-1">
          <div className="text-xs font-semibold">Attachments</div>
          <ToolTip
            title="Drag cards to associate, unassociate, or move between insurances"
            placement="top"
          >
            <span
              className="flex h-4 w-4 items-center justify-center text-gray-400"
              aria-label="Insurance attachment drag hint"
            >
              <InfoOutlinedIcon className="!h-4 !w-4 text-gray-500" />
            </span>
          </ToolTip>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Spinner size={16} />
            Loading attachments...
          </div>
        ) : (
          <div
            ref={dropAreaRef}
            className="relative"
          >
            <FileDragOverlayProvider
              targetRef={dropAreaRef}
              disabled={isInteractionDisabled || !hasInsuranceId}
              isUploading={isUploading}
              onDropFile={async (file: File | File[]) => {
                const files = Array.isArray(file) ? file : [file];
                const localFiles = await toLocalUrls(files);
                await uploadFiles(localFiles);
              }}
              onReject={(reason) => {
                if (reason === 'type') {
                  notify({
                    variant: 'error',
                    message: 'This type of file is not supported.',
                  });
                }
              }}
              messages={{
                default: 'Drop insurance card to upload',
                uploading: 'Uploading insurance card...',
                supportedHint: '',
              }}
              backgroundClassName="bg-white/80"
              dropzoneAccept={dropzoneAccept}
              multiple
              animate
              renderOverlayContent={({ isError, message }) => (
                <div className="relative flex flex-row items-center gap-2 px-2 py-1 bg-white/60">
                  <UploadIcon
                    className={clsx(
                      '!w-5 !h-5',
                      { 'text-red-500': isError, 'text-blue-500': !isError },
                    )}
                  />
                  <div className="flex flex-col items-start text-left">
                    <div
                      className={clsx(
                        'text-xs font-semibold',
                        isError ? 'text-red-700' : 'text-gray-800',
                      )}
                    >
                      {message}
                    </div>
                  </div>
                </div>
              )}
            >
              <Droppable
                droppableId={droppableId}
                direction="horizontal"
                type="insurance-card"
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={clsx(
                      'flex min-h-[64px] flex-wrap items-center gap-2 rounded-md border p-1',
                      'transition-all duration-150',
                      {
                        'border-blue-300 border-solid bg-blue-50/50 shadow-md shadow-blue-100/80 ring-1 ring-blue-200/70 -translate-y-0.5':
                          snapshot.isDraggingOver && !isSourceContainer,
                        'border-gray-200 bg-gray-50/40':
                          snapshot.isDraggingOver && isSourceContainer,
                        'border-blue-200 border-dashed bg-blue-50/20':
                          isValidMoveTarget && !snapshot.isDraggingOver,
                        'border-transparent':
                          !snapshot.isDraggingOver && !isValidMoveTarget,
                        'opacity-60 pointer-events-none': isInteractionDisabled,
                      },
                    )}
                  >
                    {draggableAttachments.map((attachment, index) => (
                      <InsuranceCardMediaThumbnail
                        key={attachment.dragId}
                        item={attachment}
                        index={index}
                        isDragDisabled={isInteractionDisabled}
                        isPositionLocked={isDraggingInsuranceCard}
                        className="h-16 w-16"
                      />
                    ))}
                    {isSourceContainer ? provided.placeholder : (
                      <div className="hidden">
                        {provided.placeholder}
                      </div>
                    )}
                    {snapshot.isDraggingOver && !isSourceContainer && (
                      <p className="px-2 text-xs font-semibold text-blue-700">
                        Drop to associate card
                      </p>
                    )}
                    {!isDraggingInsuranceCard && (
                      <InsuranceAttachmentActions
                        insuranceId={insuranceId}
                        patientId={patientId}
                        onUploadSuccess={refetch}
                        disabled={isInteractionDisabled}
                      />
                    )}
                  </div>
                )}
              </Droppable>
            </FileDragOverlayProvider>
          </div>
        )}
      </div>
    </div>
  ) : null;
}
